'use client'

import { supabase } from '@/lib/supabase'
import { mapRowToResult, searchExact } from '@/lib/search-service'
import { searchResultToGlossaryEntry } from './glossary'
import {
  createDictionaryLookup,
  extractChineseCandidates,
  type DictionaryLookup
} from './tokenizer'

const CANDIDATE_LOOKUP_CONCURRENCY = 6
const ONLINE_LOOKUP_CHUNK_SIZE = 80
const candidateLookupCache = new Map<
  string,
  Promise<ReturnType<typeof searchResultToGlossaryEntry>[]>
>()

function lookupCandidate(candidate: string, isOffline: boolean) {
  const cacheKey = `${isOffline ? 'offline' : 'remote'}:${candidate}`
  const cached = candidateLookupCache.get(cacheKey)
  if (cached) return cached

  const lookup = searchExact(candidate, 'simplified', isOffline).then(
    (results) => results.slice(0, 4).map(searchResultToGlossaryEntry)
  )
  candidateLookupCache.set(cacheKey, lookup)
  return lookup
}

async function lookupCandidatesOnline(
  candidates: string[],
  onProgress?: (progress: { completed: number; total: number }) => void
) {
  const selectWithTranslations = `
    *,
    entry_translations!left (
      locale,
      definitions,
      examples
    )
  `
  const entriesById = new Map<
    number,
    ReturnType<typeof searchResultToGlossaryEntry>
  >()
  const total = candidates.length
  let completed = 0

  for (
    let index = 0;
    index < candidates.length;
    index += ONLINE_LOOKUP_CHUNK_SIZE
  ) {
    const chunk = candidates.slice(index, index + ONLINE_LOOKUP_CHUNK_SIZE)
    const [simpData, tradData] = await Promise.all([
      supabase
        .from('dict_entries')
        .select(selectWithTranslations)
        .in('simplified', chunk)
        .eq('entry_translations.locale', 'en')
        .order('frequency', { ascending: true }),
      supabase
        .from('dict_entries')
        .select(selectWithTranslations)
        .in('traditional', chunk)
        .eq('entry_translations.locale', 'en')
        .order('frequency', { ascending: true })
    ])

    if (simpData.error) throw simpData.error
    if (tradData.error) throw tradData.error

    for (const row of [...(simpData.data ?? []), ...(tradData.data ?? [])]) {
      const entry = searchResultToGlossaryEntry(mapRowToResult(row))
      entriesById.set(entry.id, entry)
    }

    completed += chunk.length
    onProgress?.({ completed: Math.min(completed, total), total })
  }

  return [...entriesById.values()]
}

export async function buildDictionaryLookupFromTexts(
  texts: string[],
  options: {
    isOffline?: boolean
    maxWordLength?: number
    onProgress?: (progress: { completed: number; total: number }) => void
  } = {}
): Promise<DictionaryLookup> {
  const candidates = extractChineseCandidates(texts, {
    maxWordLength: options.maxWordLength
  })
  const entriesById = new Map<
    number,
    ReturnType<typeof searchResultToGlossaryEntry>
  >()
  const total = candidates.length
  let nextIndex = 0
  let completed = 0
  const isOffline = Boolean(options.isOffline)

  if (!isOffline) {
    return createDictionaryLookup(
      await lookupCandidatesOnline(candidates, options.onProgress)
    )
  }

  async function worker() {
    while (nextIndex < candidates.length) {
      const index = nextIndex
      nextIndex += 1

      const candidate = candidates[index]
      const entries = await lookupCandidate(candidate, isOffline)

      for (const entry of entries) {
        entriesById.set(entry.id, entry)
      }

      completed += 1
      options.onProgress?.({ completed, total })
    }
  }

  await Promise.all(
    Array.from(
      { length: Math.min(CANDIDATE_LOOKUP_CONCURRENCY, candidates.length) },
      worker
    )
  )

  return createDictionaryLookup([...entriesById.values()])
}
